import { Schema } from "effect";
import {
  DesktopSshAliasSchema,
  DesktopSshCanonicalRootSchema,
  DesktopSshCursorSchema,
  DesktopSshFileEntrySchema,
  DesktopSshFileErrorPayloadSchema,
  DesktopSshFileScopeSchema,
  DesktopSshListLimitSchema,
  DesktopSshRelativeDirectoryPathSchema,
  DesktopSshRelativeFilePathSchema,
  DesktopSshResolvedHostnameSchema,
  DesktopSshResolvedPortSchema,
  DesktopSshResolvedUsernameSchema,
  DesktopSshTextByteLimitSchema,
  DESKTOP_SSH_MAX_LIST_LIMIT,
  DESKTOP_SSH_MAX_TEXT_BYTES,
} from "./ssh-files";
import { compareUtf8Text, utf8ByteLength } from "./utf8";

export const SshFileRelayBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const SSH_FILE_RELAY_MAX_SESSIONS_PER_PROJECT = 8;
export const SSH_FILE_RELAY_MAX_SOCKET_MESSAGE_BYTES = 2 * 1_024 * 1_024;
export const SSH_FILE_RELAY_MAX_HTTP_RESPONSE_BYTES = 2 * 1_024 * 1_024;

const uuidV4 = "[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
const UuidV4Schema = (prefix: string, label: string) =>
  Schema.String.check(
    Schema.makeFilter(
      (value) => new RegExp(`^${prefix}${uuidV4}$`, "u").test(value) || `must be ${label}`,
    ),
  );

export const SshFileRelayRequestIdSchema = UuidV4Schema(
  "sshrequest_",
  "an SSH file relay request ID",
);
export const SshFileRelayCommandIdSchema = UuidV4Schema("sshcmd_", "an SSH file relay command ID");
export const SshFileRelaySessionIdSchema = UuidV4Schema(
  "sshrelay_",
  "an SSH file relay session ID",
);
export const SshFileRelayTicketProtocolSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      /^papersflow\.ssh-files\.[A-Za-z0-9_-]{43}$/u.test(value) ||
      "must be a one-use SSH file relay WebSocket protocol",
  ),
);

export const SshFileRelayDesktopSessionSchema = Schema.Struct({
  version: Schema.Literal(1),
  scope: DesktopSshFileScopeSchema,
  alias: DesktopSshAliasSchema,
  root: DesktopSshCanonicalRootSchema,
  hostname: DesktopSshResolvedHostnameSchema,
  username: DesktopSshResolvedUsernameSchema,
  port: DesktopSshResolvedPortSchema,
});

const RequestScopeFields = {
  requestId: SshFileRelayRequestIdSchema,
  scope: DesktopSshFileScopeSchema,
} as const;

export const SshFileRelayShareRequestSchema = Schema.Struct({
  ...RequestScopeFields,
  session: SshFileRelayDesktopSessionSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      (request.scope.workspaceId === request.session.scope.workspaceId &&
        request.scope.projectId === request.session.scope.projectId) ||
      "shared session scope must match the authorized project scope",
  ),
);

export const SshFileRelayShareTicketSchema = Schema.Struct({
  version: Schema.Literal(1),
  requestId: SshFileRelayRequestIdSchema,
  subprotocol: SshFileRelayTicketProtocolSchema,
});

export const SshFileRelaySessionSchema = Schema.Struct({
  version: Schema.Literal(1),
  sessionId: SshFileRelaySessionIdSchema,
  scope: DesktopSshFileScopeSchema,
  alias: DesktopSshAliasSchema,
  root: DesktopSshCanonicalRootSchema,
  hostname: DesktopSshResolvedHostnameSchema,
  username: DesktopSshResolvedUsernameSchema,
  port: DesktopSshResolvedPortSchema,
});

export const SshFileRelayListSessionsRequestSchema = Schema.Struct(RequestScopeFields);
export const SshFileRelayListSessionsResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  ...RequestScopeFields,
  sessions: Schema.Array(SshFileRelaySessionSchema).check(
    Schema.isMaxLength(SSH_FILE_RELAY_MAX_SESSIONS_PER_PROJECT),
  ),
}).check(
  Schema.makeFilter((result) => {
    let previous: string | undefined;
    for (const session of result.sessions) {
      if (
        session.scope.workspaceId !== result.scope.workspaceId ||
        session.scope.projectId !== result.scope.projectId ||
        (previous !== undefined && compareUtf8Text(session.sessionId, previous) <= 0)
      ) {
        return "sessions must be unique, ascending, and inside the requested project scope";
      }
      previous = session.sessionId;
    }
    return true;
  }),
);

const validDirectoryPage = (result: {
  readonly directory: string;
  readonly entries: readonly {
    readonly name: string;
    readonly relativePath: string;
  }[];
  readonly nextCursor?: string | undefined;
}) => {
  let previous: string | undefined;
  for (const entry of result.entries) {
    const expectedPath = result.directory ? `${result.directory}/${entry.name}` : entry.name;
    if (
      entry.relativePath !== expectedPath ||
      (previous !== undefined && compareUtf8Text(entry.name, previous) <= 0)
    ) {
      return "entries must be unique ascending direct children of the requested directory";
    }
    previous = entry.name;
  }
  return (
    result.nextCursor === undefined ||
    (result.entries.length > 0 && result.nextCursor === result.entries.at(-1)?.name) ||
    "next cursor must identify the last returned entry"
  );
};

export const SshFileRelayListDirectoryRequestSchema = Schema.Struct({
  ...RequestScopeFields,
  sessionId: SshFileRelaySessionIdSchema,
  relativePath: DesktopSshRelativeDirectoryPathSchema,
  cursor: Schema.optionalKey(DesktopSshCursorSchema),
  limit: DesktopSshListLimitSchema,
});

export const SshFileRelayListDirectoryResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  requestId: SshFileRelayRequestIdSchema,
  sessionId: SshFileRelaySessionIdSchema,
  directory: DesktopSshRelativeDirectoryPathSchema,
  entries: Schema.Array(DesktopSshFileEntrySchema).check(
    Schema.isMaxLength(DESKTOP_SSH_MAX_LIST_LIMIT),
  ),
  nextCursor: Schema.optionalKey(DesktopSshCursorSchema),
}).check(Schema.makeFilter(validDirectoryPage));

export const SshFileRelayReadTextFileRequestSchema = Schema.Struct({
  ...RequestScopeFields,
  sessionId: SshFileRelaySessionIdSchema,
  relativePath: DesktopSshRelativeFilePathSchema,
  maxBytes: DesktopSshTextByteLimitSchema,
});

export const SshFileRelayReadTextFileResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  requestId: SshFileRelayRequestIdSchema,
  sessionId: SshFileRelaySessionIdSchema,
  relativePath: DesktopSshRelativeFilePathSchema,
  content: Schema.String,
  encoding: Schema.Literal("utf-8"),
  sizeBytes: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(DESKTOP_SSH_MAX_TEXT_BYTES),
  ),
}).check(
  Schema.makeFilter(
    (result) =>
      utf8ByteLength(result.content) === result.sizeBytes ||
      "sizeBytes must equal the UTF-8 content byte length",
  ),
);

export const SshFileRelayCloseRequestSchema = Schema.Struct({
  ...RequestScopeFields,
  sessionId: SshFileRelaySessionIdSchema,
});
export const SshFileRelayCloseResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  requestId: SshFileRelayRequestIdSchema,
  sessionId: SshFileRelaySessionIdSchema,
});

export const SshFileRelayErrorCodeSchema = Schema.Literals([
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "INVALID_INPUT",
  "DEVICE_OFFLINE",
  "BUSY",
  "TIMEOUT",
  "CANCELLED",
  "INVALID_RESPONSE",
  "UNAVAILABLE",
  "REMOTE_ERROR",
]);
const SshFileRelayErrorFields = {
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
} as const;
export const SshFileRelayErrorSchema = Schema.Union([
  Schema.Struct({
    code: Schema.Literals([
      "UNAUTHENTICATED",
      "FORBIDDEN",
      "INVALID_INPUT",
      "DEVICE_OFFLINE",
      "BUSY",
      "TIMEOUT",
      "CANCELLED",
      "INVALID_RESPONSE",
      "UNAVAILABLE",
    ]),
    ...SshFileRelayErrorFields,
  }),
  Schema.Struct({
    code: Schema.Literal("REMOTE_ERROR"),
    ...SshFileRelayErrorFields,
    remoteError: DesktopSshFileErrorPayloadSchema,
  }),
]);

const DeviceCommandFields = {
  version: Schema.Literal(1),
  commandId: SshFileRelayCommandIdSchema,
  sessionId: SshFileRelaySessionIdSchema,
} as const;
export const SshFileRelayDeviceReadySchema = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("ready"),
  session: SshFileRelaySessionSchema,
});
export const SshFileRelayDeviceListCommandSchema = Schema.Struct({
  ...DeviceCommandFields,
  type: Schema.Literal("list-directory"),
  relativePath: DesktopSshRelativeDirectoryPathSchema,
  cursor: Schema.optionalKey(DesktopSshCursorSchema),
  limit: DesktopSshListLimitSchema,
});
export const SshFileRelayDeviceReadCommandSchema = Schema.Struct({
  ...DeviceCommandFields,
  type: Schema.Literal("read-text-file"),
  relativePath: DesktopSshRelativeFilePathSchema,
  maxBytes: DesktopSshTextByteLimitSchema,
});
export const SshFileRelayDeviceCloseCommandSchema = Schema.Struct({
  ...DeviceCommandFields,
  type: Schema.Literal("close"),
});
export const SshFileRelayDeviceCommandSchema = Schema.Union([
  SshFileRelayDeviceListCommandSchema,
  SshFileRelayDeviceReadCommandSchema,
  SshFileRelayDeviceCloseCommandSchema,
]);
export const SshFileRelayServerMessageSchema = Schema.Union([
  SshFileRelayDeviceReadySchema,
  SshFileRelayDeviceCommandSchema,
]);

const DeviceResultFields = {
  version: Schema.Literal(1),
  commandId: SshFileRelayCommandIdSchema,
  sessionId: SshFileRelaySessionIdSchema,
} as const;
export const SshFileRelayDeviceListSuccessSchema = Schema.Struct({
  ...DeviceResultFields,
  type: Schema.Literal("list-directory-succeeded"),
  directory: DesktopSshRelativeDirectoryPathSchema,
  entries: Schema.Array(DesktopSshFileEntrySchema).check(
    Schema.isMaxLength(DESKTOP_SSH_MAX_LIST_LIMIT),
  ),
  nextCursor: Schema.optionalKey(DesktopSshCursorSchema),
}).check(Schema.makeFilter(validDirectoryPage));
export const SshFileRelayDeviceReadSuccessSchema = Schema.Struct({
  ...DeviceResultFields,
  type: Schema.Literal("read-text-file-succeeded"),
  relativePath: DesktopSshRelativeFilePathSchema,
  content: Schema.String,
  encoding: Schema.Literal("utf-8"),
  sizeBytes: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(DESKTOP_SSH_MAX_TEXT_BYTES),
  ),
}).check(
  Schema.makeFilter(
    (result) =>
      utf8ByteLength(result.content) === result.sizeBytes ||
      "sizeBytes must equal the UTF-8 content byte length",
  ),
);
export const SshFileRelayDeviceCloseSuccessSchema = Schema.Struct({
  ...DeviceResultFields,
  type: Schema.Literal("close-succeeded"),
});
export const SshFileRelayDeviceFailureSchema = Schema.Struct({
  ...DeviceResultFields,
  type: Schema.Literal("failed"),
  operation: Schema.Literals(["list-directory", "read-text-file", "close"]),
  error: DesktopSshFileErrorPayloadSchema,
});
export const SshFileRelayDeviceResultSchema = Schema.Union([
  SshFileRelayDeviceListSuccessSchema,
  SshFileRelayDeviceReadSuccessSchema,
  SshFileRelayDeviceCloseSuccessSchema,
  SshFileRelayDeviceFailureSchema,
]);

export type SshFileRelayRequestId = typeof SshFileRelayRequestIdSchema.Type;
export type SshFileRelayCommandId = typeof SshFileRelayCommandIdSchema.Type;
export type SshFileRelaySessionId = typeof SshFileRelaySessionIdSchema.Type;
export type SshFileRelayDesktopSession = typeof SshFileRelayDesktopSessionSchema.Type;
export type SshFileRelayShareRequest = typeof SshFileRelayShareRequestSchema.Type;
export type SshFileRelayShareTicket = typeof SshFileRelayShareTicketSchema.Type;
export type SshFileRelaySession = typeof SshFileRelaySessionSchema.Type;
export type SshFileRelayListSessionsRequest = typeof SshFileRelayListSessionsRequestSchema.Type;
export type SshFileRelayListSessionsResult = typeof SshFileRelayListSessionsResultSchema.Type;
export type SshFileRelayListDirectoryRequest = typeof SshFileRelayListDirectoryRequestSchema.Type;
export type SshFileRelayListDirectoryResult = typeof SshFileRelayListDirectoryResultSchema.Type;
export type SshFileRelayReadTextFileRequest = typeof SshFileRelayReadTextFileRequestSchema.Type;
export type SshFileRelayReadTextFileResult = typeof SshFileRelayReadTextFileResultSchema.Type;
export type SshFileRelayCloseRequest = typeof SshFileRelayCloseRequestSchema.Type;
export type SshFileRelayCloseResult = typeof SshFileRelayCloseResultSchema.Type;
export type SshFileRelayErrorCode = typeof SshFileRelayErrorCodeSchema.Type;
export type SshFileRelayError = typeof SshFileRelayErrorSchema.Type;
export type SshFileRelayDeviceCommand = typeof SshFileRelayDeviceCommandSchema.Type;
export type SshFileRelayServerMessage = typeof SshFileRelayServerMessageSchema.Type;
export type SshFileRelayDeviceResult = typeof SshFileRelayDeviceResultSchema.Type;

const codec = <S extends Schema.ConstraintCodec<unknown, unknown>>(schema: S) => ({
  decode: Schema.decodeUnknownSync(schema, SshFileRelayBoundaryParseOptions),
  encode: Schema.encodeSync(schema, SshFileRelayBoundaryParseOptions),
});

export const SshFileRelayShareRequestCodec = codec(SshFileRelayShareRequestSchema);
export const SshFileRelayShareTicketCodec = codec(SshFileRelayShareTicketSchema);
export const SshFileRelaySessionCodec = codec(SshFileRelaySessionSchema);
export const SshFileRelayListSessionsRequestCodec = codec(SshFileRelayListSessionsRequestSchema);
export const SshFileRelayListSessionsResultCodec = codec(SshFileRelayListSessionsResultSchema);
export const SshFileRelayListDirectoryRequestCodec = codec(SshFileRelayListDirectoryRequestSchema);
export const SshFileRelayListDirectoryResultCodec = codec(SshFileRelayListDirectoryResultSchema);
export const SshFileRelayReadTextFileRequestCodec = codec(SshFileRelayReadTextFileRequestSchema);
export const SshFileRelayReadTextFileResultCodec = codec(SshFileRelayReadTextFileResultSchema);
export const SshFileRelayCloseRequestCodec = codec(SshFileRelayCloseRequestSchema);
export const SshFileRelayCloseResultCodec = codec(SshFileRelayCloseResultSchema);
export const SshFileRelayErrorCodec = codec(SshFileRelayErrorSchema);
export const SshFileRelayServerMessageCodec = codec(SshFileRelayServerMessageSchema);
export const SshFileRelayDeviceResultCodec = codec(SshFileRelayDeviceResultSchema);
